import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { Subscription, debounceTime, distinctUntilChanged, filter } from 'rxjs';
import findKey from 'lodash-es/findKey';
import { AddressAutocompleteModule } from '@ee/common/address-autocomplete';
import { STATES } from '@ee/common/constants';
import { AddressSuggestion, County } from '@ee/common/models';
import { CountyService } from '@ee/common/services';
import { CompositePart } from './composites';

/**
 * The property address, as one question.
 *
 * The reason it is a composite and not seven fields is behavioural, not visual: answering the street
 * line fills four of the others, and the county follows from the ZIP. A form that asked for all seven
 * separately would be asking a client to type what it already knows.
 *
 * Split out of the renderer rather than added to it. The renderer draws placements and is used by the
 * builder's preview, where a live geocoder and a county lookup are exactly what must not run — a canvas
 * that redraws as you drag would issue a search per frame. So the renderer keeps the inert drawing and
 * this owns the live behaviour.
 */
@Component({
  selector: 'ee-address-block',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, MatFormFieldModule, MatInputModule, MatSelectModule, AddressAutocompleteModule],
  template: `
    <div class="address">
      <div class="address__grid">
        <div class="address__street">
          <ee-address-autocomplete [formControl]="control('property_street_address')"
                                   [placeholder]="labelOf('property_street_address')"
                                   [required]="isRequired('property_street_address')"
                                   [selectedState]="value('property_state')"
                                   [selectedCity]="value('property_city')"
                                   [compact]="true"
                                   (selectionChange)="onSuggestion($event)"></ee-address-autocomplete>
        </div>

        @if (has('property_unit_type')) {
          <mat-form-field appearance="outline" class="compact address__unit-type">
            <mat-label>{{ labelOf('property_unit_type') }}</mat-label>
            <mat-select [formControl]="control('property_unit_type')">
              @for (type of unitTypes; track type) {
                <mat-option [value]="type">{{ type }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
        }

        @if (has('property_unit_number')) {
          <mat-form-field appearance="outline" class="compact address__unit-number">
            <mat-label>{{ labelOf('property_unit_number') }}</mat-label>
            <input matInput [formControl]="control('property_unit_number')" />
          </mat-form-field>
        }

        <mat-form-field appearance="outline" class="compact address__zip">
          <mat-label>{{ labelOf('property_zip_code') }}</mat-label>
          <input matInput inputmode="numeric" maxlength="5" [formControl]="control('property_zip_code')" />
        </mat-form-field>

        <mat-form-field appearance="outline" class="compact address__city">
          <mat-label>{{ labelOf('property_city') }}</mat-label>
          <input matInput [formControl]="control('property_city')" />
        </mat-form-field>

        <mat-form-field appearance="outline" class="compact address__state">
          <mat-label>{{ labelOf('property_state') }}</mat-label>
          <mat-select [formControl]="control('property_state')">
            @for (state of states; track state) {
              <mat-option [value]="state">{{ state }}</mat-option>
            }
          </mat-select>
        </mat-form-field>

        <mat-form-field appearance="outline" class="compact address__county">
          <mat-label>{{ labelOf('property_county') }}</mat-label>
          <!--
            A select, not a readout. The ZIP narrows it, but a ZIP can straddle two counties and only
            the client knows which one the unit is in — deriving one and hiding the choice is how a
            filing lands in the wrong court.
          -->
          <mat-select [formControl]="control('property_county')">
            @for (county of counties(); track county.id) {
              <mat-option [value]="county.name">{{ county.county_name_override || county.name }}</mat-option>
            }
          </mat-select>
          @if (looking()) {
            <mat-hint>Looking up counties…</mat-hint>
          } @else if (!counties().length && value('property_zip_code')) {
            <mat-hint>No county matched that ZIP — pick a state to list them.</mat-hint>
          }
        </mat-form-field>
      </div>
    </div>
  `,
  styleUrl: './address-block.component.scss'
})
export class AddressBlockComponent implements OnInit, OnDestroy {
  @Input({ required: true }) form!: FormGroup;

  @Input() parts: CompositePart[] = [];

  @Input() unitTypes: string[] = [];

  private readonly countyService = inject(CountyService);
  private readonly subs: Subscription[] = [];
  /** The one lookup in flight. A new one cancels it: its answer is about an address the form has left. */
  private lookup?: Subscription;

  protected readonly states = Object.keys(STATES);
  protected readonly counties = signal<County[]>([]);
  protected readonly looking = signal(false);

  ngOnInit(): void {
    /*
     * Five digits, debounced, and only when it changed.
     *
     * A lookup per keystroke is five requests for one ZIP, and the first four answer a prefix. The
     * autocomplete sets the ZIP with `emitEvent: false` for the same reason — otherwise selecting a
     * suggestion fires this as well and the two race.
     */
    const zip = this.form.get('property_zip_code');
    if (zip) {
      this.subs.push(
        zip.valueChanges
          .pipe(
            debounceTime(300),
            distinctUntilChanged(),
            filter((value: string) => (value ?? '').length === 5)
          )
          .subscribe(value => this.lookupByZip(value))
      );
      if ((zip.value ?? '').length === 5) {
        this.lookupByZip(zip.value);
      }
    }

    // A state with no ZIP still narrows it from every county in the country to one state's worth.
    const state = this.form.get('property_state');
    if (state) {
      this.subs.push(
        state.valueChanges.subscribe(value => {
          if (!(this.form.get('property_zip_code')?.value ?? '').length && value) {
            this.lookupByState(value);
          }
        })
      );
    }
  }

  ngOnDestroy(): void {
    this.subs.forEach(sub => sub.unsubscribe());
    this.lookup?.unsubscribe();
  }

  protected has(key: string): boolean {
    return this.parts.some(part => part.key === key) && !!this.form.get(key);
  }

  protected labelOf(key: string): string {
    return this.parts.find(part => part.key === key)?.label ?? key;
  }

  protected control(key: string): FormControl {
    return (this.form.get(key) as FormControl) ?? new FormControl(null);
  }

  protected value(key: string): string {
    return (this.form.get(key)?.value ?? '') as string;
  }

  /** `Validators.required` is a stable reference, so the control can simply be asked. */
  protected isRequired(key: string): boolean {
    return this.form.get(key)?.hasValidator(Validators.required) === true;
  }

  /**
   * A chosen suggestion fills the rest of the block.
   *
   * `emitEvent: false` on the ZIP: the county lookup runs once, here, with the state already set.
   * Letting the ZIP's own subscription fire as well means two lookups, and the slower one wins.
   */
  protected onSuggestion(suggestion: AddressSuggestion): void {
    if (!suggestion) {
      return;
    }
    const state = findKey(STATES, name => name === suggestion.state_full) ?? suggestion.state;

    this.form.get('property_street_address')?.setValue(suggestion.street_address, { emitEvent: false });
    this.form.get('property_city')?.setValue(suggestion.city, { emitEvent: false });
    this.form.get('property_state')?.setValue(state, { emitEvent: false });
    this.form.get('property_zip_code')?.setValue(suggestion.zip_code, { emitEvent: false });
    // Only if the form carries them: they are not part of the block, but the geocode is the one moment
    // they are known and re-deriving them later costs another request.
    this.form.get('property_lat')?.setValue(suggestion.lat, { emitEvent: false });
    this.form.get('property_lng')?.setValue(suggestion.lng, { emitEvent: false });

    this.lookupByZip(suggestion.zip_code);
  }

  private lookupByZip(zip: string): void {
    this.lookup?.unsubscribe();
    this.looking.set(true);
    this.lookup = this.countyService.getCountiesByZipCode(zip).subscribe({
      next: counties => this.settle(counties),
      // Left for the client to choose from the state instead. A failed lookup must not clear a county
      // they already picked.
      error: () => this.looking.set(false)
    });
  }

  private lookupByState(state: string): void {
    this.lookup?.unsubscribe();
    this.looking.set(true);
    this.lookup = this.countyService.getCountiesByState(state).subscribe({
      next: counties => this.settle(counties),
      error: () => this.looking.set(false)
    });
  }

  /**
   * Take the candidates, and choose for the client only when there is nothing to choose.
   *
   * One county means no question worth asking. More than one means a ZIP that straddles a boundary, and
   * picking the first would put the filing in a court the client never saw named.
   */
  private settle(counties: County[]): void {
    this.looking.set(false);
    this.counties.set(counties ?? []);

    const control = this.form.get('property_county');
    if (!control) {
      return;
    }
    const names = (counties ?? []).map(county => county.name);
    if (counties?.length === 1) {
      control.setValue(names[0]);
      return;
    }
    // A previous answer that the new candidates do not contain is stale — the address moved.
    if (control.value && !names.includes(control.value)) {
      control.setValue(null);
    }
  }
}
